/**
 * Contrast validation utility for accessibility compliance
 * Ensures all text/background pairs meet WCAG AA standards (≥4.5:1)
 */

export interface ContrastResult {
  ratio: number;
  isAccessible: boolean;
  level: 'AAA' | 'AA' | 'A' | 'FAIL';
}

/**
 * Convert hex color to RGB
 */
function hexToRgb(hex: string): [number, number, number] | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? [
    parseInt(result[1], 16),
    parseInt(result[2], 16),
    parseInt(result[3], 16)
  ] : null;
}

/**
 * Calculate relative luminance according to WCAG guidelines
 */
function getLuminance(r: number, g: number, b: number): number {
  const [rs, gs, bs] = [r, g, b].map(c => {
    c = c / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

/**
 * Calculate contrast ratio between two colors
 */
export function calculateContrastRatio(color1: string, color2: string): number {
  const rgb1 = hexToRgb(color1);
  const rgb2 = hexToRgb(color2);
  
  if (!rgb1 || !rgb2) {
    console.warn('Invalid color format provided to contrast calculator');
    return 0;
  }
  
  const lum1 = getLuminance(rgb1[0], rgb1[1], rgb1[2]);
  const lum2 = getLuminance(rgb2[0], rgb2[1], rgb2[2]);
  
  const brightest = Math.max(lum1, lum2);
  const darkest = Math.min(lum1, lum2);
  
  return (brightest + 0.05) / (darkest + 0.05);
}

/**
 * Validate contrast ratio for accessibility
 */
export function validateContrast(foreground: string, background: string): ContrastResult {
  const ratio = calculateContrastRatio(foreground, background);
  
  let level: ContrastResult['level'];
  let isAccessible: boolean;
  
  if (ratio >= 7) {
    level = 'AAA';
    isAccessible = true;
  } else if (ratio >= 4.5) {
    level = 'AA';
    isAccessible = true;
  } else if (ratio >= 3) {
    level = 'A';
    isAccessible = false;
  } else {
    level = 'FAIL';
    isAccessible = false;
  }
  
  return { ratio, isAccessible, level };
}

/**
 * Batch validate multiple color pairs
 */
export function validateMultipleContrasts(colorPairs: Array<[string, string, string]>): Record<string, ContrastResult> {
  const results: Record<string, ContrastResult> = {};
  
  colorPairs.forEach(([name, foreground, background]) => {
    results[name] = validateContrast(foreground, background);
  });
  
  return results;
}

/**
 * Get suggested adjustments for failing contrast ratios
 */
export function suggestContrastAdjustments(
  foreground: string, 
  background: string
): { adjustedForeground: string; adjustedBackground: string } | null {
  const result = validateContrast(foreground, background);
  
  if (result.isAccessible) {
    return null;
  }
  
  // Simple adjustment: darken foreground or lighten background
  const fgRgb = hexToRgb(foreground);
  const bgRgb = hexToRgb(background);
  
  if (!fgRgb || !bgRgb) {
    return null;
  }
  
  // For now, return simplified adjustments
  // In production, this would implement proper color space adjustments
  const adjustedForeground = result.ratio < 2 
    ? '#000000'  // Use black for very poor contrast
    : foreground;
    
  const adjustedBackground = result.ratio < 2
    ? '#FFFFFF'  // Use white background for very poor contrast
    : background;
  
  return { adjustedForeground, adjustedBackground };
}